import React, { useState, useEffect, useCallback } from 'react';
import PlayerAvatar from './PlayerAvatar';
import { formatHistoryDate } from './HistoryShared';

// Скрытая вкладка "Профиль действий" — открывается тройным быстрым нажатием
// по надписи "Azon" в шапке и только для суперадмина (см. App.js: isSuperAdmin +
// проверка requester_tg_id на сервере). Показывает журнал ВСЕХ привилегированных
// действий. Если пользователь не выбран — единая лента действий всех
// привилегированных пользователей (по времени, при равном времени — по алфавиту
// ников). При выборе пользователя — только его действия. Постранично (по 30,
// свежие сверху), с подробным описанием, фильтрами и цветовыми категориями по
// важности действия.
const ROLE_LABELS = {
  banker: 'Банкир',
  warehouse: 'Работник склада',
  seller: 'Продавец',
  moderator: 'Модератор'
};

// Категории действий для фильтра — ключи должны совпадать с ACTION_LOG_CATEGORIES
// на сервере (server/routes/admin.js), сами SQL-паттерны там же, здесь только подписи.
const DEFAULT_FILTERS = { category: '', severity: '', dateFrom: '', dateTo: '', q: '' };
const CATEGORY_OPTIONS = [
  { value: '', label: 'Все категории' },
  { value: 'roles', label: 'Роли' },
  { value: 'settings', label: 'Настройки' },
  { value: 'backups', label: 'Бэкапы' },
  { value: 'announce', label: 'Рассылки' },
  { value: 'invoice', label: 'Инвойсы' },
  { value: 'player', label: 'Действия с игроками' },
  { value: 'account', label: 'Действия со счетами' },
  { value: 'withdrawal', label: 'Заявки на вывод' },
  { value: 'deposit', label: 'Заявки на пополнение' },
  { value: 'order', label: 'Заказы' },
  { value: 'market_request', label: 'Заявки склада' },
  { value: 'market_item', label: 'Товары маркетплейса' },
  { value: 'ad', label: 'Реклама' },
  { value: 'decrypt', label: 'Дешифратор' },
  { value: 'other', label: 'Прочее' }
];

// Категории по важности — ключи совпадают с severity на сервере. Названия
// заданы по смыслу («Важное» и т.д.), а не по цветам.
const SEVERITY_OPTIONS = [
  { value: '', label: 'Любая важность' },
  { value: 'critical', label: 'Важное' },
  { value: 'rejected', label: 'Отклонённые' },
  { value: 'normal', label: 'Обычные' }
];
// Подпись-«чип» и css-класс для строки по важности. normal — без цвета и без чипа.
const SEVERITY_META = {
  critical: { label: 'Важное', cls: 'sev-critical' },
  rejected: { label: 'Отклонено', cls: 'sev-rejected' },
  normal: { label: '', cls: '' }
};

function roleTags(user) {
  const tags = [];
  if (user.isSuperAdmin) tags.push('Суперадмин');
  else if (user.isAdmin) tags.push('Администратор');
  (user.roles || []).forEach(r => tags.push(ROLE_LABELS[r] || r));
  return tags;
}

const LINKABLE_OP_PREFIXES = ['order_', 'withdrawal_', 'deposit_', 'market_request_'];
function isLinkableOp(type) {
  return LINKABLE_OP_PREFIXES.some(p => String(type || '').startsWith(p));
}

// Разбивает описание действия так, чтобы первый идентификатор вида "#XXXX" стал
// кликабельным. По клику суперадмина перекидывает в раздел заявок админ-панели
// прямо к этой заявке (см. onOpenOperation -> App.js -> Admin requestFocus).
function renderActionDescription(action, onOpenOperation) {
  const text = action.description || '';
  if (!onOpenOperation || !isLinkableOp(action.action_type)) return text;
  const m = text.match(/#([^\s.,;]+)/);
  if (!m) return text;
  const opId = m[1];
  const open = () => onOpenOperation({ type: action.action_type, id: opId });
  return (
    <>
      {text.slice(0, m.index)}
      <span
        className="op-link"
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
      >#{opId}</span>
      {text.slice(m.index + m[0].length)}
    </>
  );
}

function AdminProf({ tgId, onInitialLoad, onOpenOperation }) {
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState('');
  // Пустая строка = «все пользователи» (единая лента).
  const [selectedTgId, setSelectedTgId] = useState('');
  const [actions, setActions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [tempFilters, setTempFilters] = useState(DEFAULT_FILTERS);
  const [filterModal, setFilterModal] = useState(false);

  useEffect(() => {
    fetch(`/api/admin/privileged-users?requester_tg_id=${encodeURIComponent(tgId)}`)
      .then(res => res.json())
      .then(data => {
        setUsers(Array.isArray(data.users) ? data.users : []);
        if (data.error) setUsersError(data.error);
      })
      .catch(() => setUsersError('Не удалось загрузить список'))
      .finally(() => { onInitialLoad && onInitialLoad(); });
  }, [tgId, onInitialLoad]);

  const fetchActions = useCallback((targetTgId, pageNum, f) => {
    setLoading(true);
    const qs = new URLSearchParams({
      requester_tg_id: tgId,
      page: pageNum
    });
    if (targetTgId) qs.set('tg_id', targetTgId);
    if (f.category) qs.set('category', f.category);
    if (f.severity) qs.set('severity', f.severity);
    if (f.dateFrom) qs.set('date_from', f.dateFrom);
    if (f.dateTo) qs.set('date_to', f.dateTo);
    if (f.q.trim()) qs.set('q', f.q.trim());
    fetch(`/api/admin/actions?${qs.toString()}`)
      .then(res => res.json())
      .then(data => {
        setActions(Array.isArray(data.actions) ? data.actions : []);
        setHasMore(!!data.hasMore);
        setTotal(data.total || 0);
      })
      .catch(() => { setActions([]); setHasMore(false); setTotal(0); })
      .finally(() => setLoading(false));
  }, [tgId]);

  // Загружаем всегда: при пустом selectedTgId — действия всех пользователей.
  useEffect(() => {
    fetchActions(selectedTgId, page, filters);
  }, [selectedTgId, page, filters, fetchActions]);

  const isFilterActive = !!(filters.category || filters.severity || filters.dateFrom || filters.dateTo || filters.q.trim());
  const allUsers = !selectedTgId;

  const applyFilters = () => {
    setFilters(tempFilters);
    setPage(1);
    setFilterModal(false);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setTempFilters(DEFAULT_FILTERS);
    setPage(1);
    setFilterModal(false);
  };

  const onSelect = (e) => {
    setSelectedTgId(e.target.value);
    setPage(1);
    window.scrollTo(0, 0);
  };

  const goPage = (next) => {
    setPage(next);
    window.scrollTo(0, 0);
  };

  const selectedUser = users.find(u => String(u.tg_id) === String(selectedTgId));

  return (
    <div>
      <h2 style={{marginTop: 0}}>Профиль действий</h2>
      <p style={{color: 'var(--text-secondary)', fontSize: '13px', marginTop: 0}}>
        Журнал всех действий, совершённых по привилегиям (недоступных обычным игрокам).
      </p>

      <div className="card">
        <label style={{fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px'}}>
          Привилегированный пользователь
        </label>
        <select
          value={selectedTgId}
          onChange={onSelect}
          style={{width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)'}}
        >
          <option value="">— Все пользователи —</option>
          {users.map(u => (
            <option key={u.tg_id} value={u.tg_id}>
              {u.nickname}{roleTags(u).length ? ` — ${roleTags(u).join(', ')}` : ''}
            </option>
          ))}
        </select>
        {usersError && <div style={{color: 'var(--danger)', marginTop: '10px', fontSize: '13px'}}>{usersError}</div>}

        {selectedUser ? (
          <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px'}}>
            <PlayerAvatar nickname={selectedUser.nickname} size={38} />
            <div>
              <div style={{fontWeight: 'bold'}}>{selectedUser.nickname}</div>
              <div style={{fontSize: '12px', color: 'var(--text-secondary)'}}>{roleTags(selectedUser).join(', ') || 'без ролей'}</div>
            </div>
          </div>
        ) : (
          <div style={{fontSize: '12px', color: 'var(--text-secondary)', marginTop: '10px'}}>
            Показаны действия всех привилегированных пользователей — по времени, при совпадении времени по алфавиту ников.
          </div>
        )}
      </div>

      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '4px 2px 10px'}}>
        <h3 style={{margin: 0}}>Действия</h3>
        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
          <span style={{fontSize: '13px', color: 'var(--text-secondary)'}}>Всего: {total}</span>
          <button className="link-btn" onClick={() => { setTempFilters(filters); setFilterModal(true); }}>
            {isFilterActive ? 'Фильтры (Активны)' : 'Фильтры'}
          </button>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Загрузка...</p>
        ) : actions.length === 0 ? (
          <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>{isFilterActive ? 'Ничего не найдено по этим фильтрам' : 'Действий пока нет'}</p>
        ) : (
          actions.map(a => {
            const meta = SEVERITY_META[a.severity] || SEVERITY_META.normal;
            return (
              <div key={a.id} className={`action-log-row ${meta.cls}`}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '3px'}}>
                  <span style={{fontSize: '12px', color: 'var(--text-secondary)'}}>
                    {formatHistoryDate(a.created_at)}
                  </span>
                  {meta.label && <span className={`sev-chip ${meta.cls}`}>{meta.label}</span>}
                </div>
                {allUsers && (
                  <div style={{display: 'flex', alignItems: 'center', gap: '7px', margin: '2px 0 5px'}}>
                    <PlayerAvatar nickname={a.admin_nickname} size={20} rounded={4} />
                    <span style={{fontSize: '12.5px', fontWeight: 600}}>{a.admin_nickname}</span>
                  </div>
                )}
                <div style={{fontSize: '14px', lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'anywhere'}}>{renderActionDescription(a, onOpenOperation)}</div>
              </div>
            );
          })
        )}
      </div>

      {(actions.length > 0 || page > 1) && (
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px', marginBottom: '20px'}}>
          <button
            className="secondary-btn"
            style={{width: 'auto', padding: '10px 20px'}}
            disabled={page === 1 || loading}
            onClick={() => goPage(page - 1)}
          >←</button>
          <span style={{fontSize: '14px', color: 'var(--text-secondary)'}}>Страница {page}</span>
          <button
            className="secondary-btn"
            style={{width: 'auto', padding: '10px 20px'}}
            disabled={!hasMore || loading}
            onClick={() => goPage(page + 1)}
          >→</button>
        </div>
      )}

      {filterModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Фильтры журнала</h3>

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Важность</label>
            <select
              value={tempFilters.severity}
              onChange={e => setTempFilters({...tempFilters, severity: e.target.value})}
              style={{width: '100%', padding: '10px', marginBottom: '15px', marginTop: '5px', borderRadius: '8px', border: '1px solid var(--border)'}}
            >
              {SEVERITY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Категория действия</label>
            <select
              value={tempFilters.category}
              onChange={e => setTempFilters({...tempFilters, category: e.target.value})}
              style={{width: '100%', padding: '10px', marginBottom: '15px', marginTop: '5px', borderRadius: '8px', border: '1px solid var(--border)'}}
            >
              {CATEGORY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Дата с</label>
            <input
              type="date"
              value={tempFilters.dateFrom}
              onChange={e => setTempFilters({...tempFilters, dateFrom: e.target.value})}
              style={{width: '100%', padding: '10px', marginBottom: '15px', marginTop: '5px', borderRadius: '8px', border: '1px solid var(--border)'}}
            />

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Дата по</label>
            <input
              type="date"
              value={tempFilters.dateTo}
              onChange={e => setTempFilters({...tempFilters, dateTo: e.target.value})}
              style={{width: '100%', padding: '10px', marginBottom: '15px', marginTop: '5px', borderRadius: '8px', border: '1px solid var(--border)'}}
            />

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Поиск по описанию</label>
            <input
              type="text"
              placeholder="Например: ник игрока, номер счёта..."
              value={tempFilters.q}
              onChange={e => setTempFilters({...tempFilters, q: e.target.value})}
              style={{marginBottom: '20px', marginTop: '5px'}}
            />

            <div className="modal-actions">
              <button className="primary-btn" onClick={applyFilters}>Применить</button>
              <button className="secondary-btn" onClick={resetFilters}>Сбросить</button>
            </div>
            <button className="link-btn" style={{width: '100%', marginTop: '15px'}} onClick={() => setFilterModal(false)}>Закрыть</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminProf;
